// Sugalarm — the custody question on the urgent takeover (cycle 447).
//
// ⚠ WHY THIS EXISTS. The server authors `custody_prompt_kind` /
// `custody_prompt_deadline` on the alert row and resolves an unanswered
// deadline itself (cycle 427). <TreatingScreen> renders that question and
// has since cycle 407 — but `NowScreen` mounts <TreatingScreen> only while
// `treating || quietWindow` is true. THE MOMENT A WINDOW ENDS, the surface
// on screen becomes the inline urgent takeover (or the collapsed
// <UrgentBanner>), and neither could render a question. So a row could
// carry a live prompt while the thing in front of the caregiver showed a
// bare alarm face with no way to answer it.
//
// This component is that question, for those two surfaces. <TreatingScreen>
// is deliberately NOT refactored onto it: it already works on phone and
// kiosk, and rewriting a working alarm surface to share code with a new one
// is how a cycle breaks the thing it came to fix.
//
// ⚠ WHAT IT MAY NEVER DO. It is a question, not an alarm control. It never
// touches the number, the level pill, Snooze or the audio loop; it never
// renders Stop (the cycle-73 floor); and it never moves the surface it
// sits on. The block — question AND actions — is OUT OF FLOW, anchored to
// the bottom of `.alarm` (see the module CSS), so the number, the pill and
// the Snooze row keep their measured `top` whether or not a question is
// pending. Putting the question in the cycle-407 subtext slot was tried
// first and measured wrong (the face rose 10.5 px on a 390-wide phone —
// the one-line question is shorter than the two-line advisory it
// replaced); see the comment above the mount in <NowScreen>.
//
// ⚠ TAKEOVER ONLY (operator decision). The collapsed <UrgentBanner> is not
// expanded to hold a question — it is a fixed single-line row (cycle 416)
// and exists so the IOB ring, trend and food stay visible while a
// correction works (cycle 187) — and there is no text-only variant: a
// question with no Yes/No is the present-and-refused shape cycle 427
// removed. The accepted gap, named: a caregiver who snoozed into the
// banner does not see a pending question THERE; the takeover is the
// surface once the window ends.
//
// ⚠ ESCAPES ARE ALWAYS REACHABLE. "I need help now" and "Resume alerts"
// can only make things LOUDER, so they render whether or not a question is
// pending. "Yes" and "No" answer a DECLARATION, so on a quiet window they
// are ABSENT rather than present-and-refused (cycle 409: the quiet screen
// makes none of the declaration's claims).
import { useCallback, useEffect, useRef, useState } from "react";

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import { recordClientEvent } from "../../lib/clientEvents";
import type { CustodyCheckinAnswer, CustodyCheckinOutcome } from "../../lib/types";
import {
  CHECKIN_REPLY_VISIBLE_MS,
  checkinQuestionText,
  checkinReplyIsTransient,
  checkinReplyText,
  fmtPromptRemaining,
  promptRemainingMs,
} from "../../lib/treatingCheckin";
import styles from "./CustodyPrompt.module.css";

/** The one surface this renders on. A `"banner"` tone was drafted and
 *  rejected (see the header) — do not add it back. */
export type CustodyPromptTone = "takeover";

export interface CustodyPromptProps {
  episode: AlarmEpisodeState;
  memberName: string | null;
  /** Wall-clock ms, passed in so the surface's own ticker drives the
   *  countdown rather than this component starting a second one. */
  nowMs: number;
  tone: CustodyPromptTone;
  /** True while the cycle-392 identity sheet is open over the takeover.
   *  The block is then `display: none` AND `.alarm` drops its
   *  `position: relative` (NowScreen.module.css), so the sheet's absolute
   *  scrim resolves against the SAME containing block it had before this
   *  cycle — the cycle-395 sheet geometry is untouched by construction
   *  (safety-invariant-reviewer, cycle 447). */
  hidden?: boolean;
}

/** The question this surface should show, or null. Pure. */
function custodyPromptText(
  episode: AlarmEpisodeState,
  memberName: string | null,
): string | null {
  const kind = episode.promptKind;
  if (!kind || !episode.checkinPending) return null;
  // A quiet window has nobody's name behind it (cycle 409), so the text
  // is composed with a null contact rather than a guessed one.
  return (
    checkinQuestionText(
      kind,
      memberName,
      episode.quietWindow ? null : episode.treatingContactName,
    ) || null
  );
}

export function CustodyPrompt({
  episode,
  memberName,
  nowMs,
  tone,
  hidden = false,
}: CustodyPromptProps) {
  const [busy, setBusy] = useState<CustodyCheckinAnswer | null>(null);
  const question = episode.promptKind;
  const text = custodyPromptText(episode, memberName);
  const pending = text != null;

  // ── the reply line: ONE door, cancel-before-write (the cycle-437 rule) ──
  // A failed send is never invisible (cycle 416): "Could not send." stays
  // until the next question or the block unmounts. A SUCCESS line is
  // transient (8 s) because the state it reports is on the row's next
  // frame; classified in treatingCheckin.ts beside the words, never here.
  const [reply, setReply] = useState<string | null>(null);
  const replyClearTimerRef = useRef<number | null>(null);
  const showReply = useCallback(
    (line: string | null, opts?: { transient?: boolean }) => {
      if (replyClearTimerRef.current != null) {
        window.clearTimeout(replyClearTimerRef.current);
        replyClearTimerRef.current = null;
      }
      setReply(line);
      if (line != null && opts?.transient) {
        replyClearTimerRef.current = window.setTimeout(() => {
          replyClearTimerRef.current = null;
          setReply(null);
        }, CHECKIN_REPLY_VISIBLE_MS);
      }
    },
    [],
  );
  // A new question replaces any reply; unmount cancels a pending clear.
  useEffect(() => {
    showReply(null);
  }, [episode.checkinEvent?.seq, showReply]);
  useEffect(
    () => () => {
      if (replyClearTimerRef.current != null) {
        window.clearTimeout(replyClearTimerRef.current);
      }
    },
    [],
  );

  const send = useCallback(
    async (answer: CustodyCheckinAnswer) => {
      if (busy === answer) return;
      setBusy(answer);
      let outcome: CustodyCheckinOutcome | "timeout" | null = null;
      try {
        outcome = await episode.checkin(answer, question);
      } finally {
        setBusy((b: CustodyCheckinAnswer | null) => (b === answer ? null : b));
      }
      if (outcome === "timeout") {
        showReply("Taking longer than usual. Try again.");
        return;
      }
      if (outcome == null) {
        // Refused (a 409 — the window or the prompt already ended) or a
        // network fault; the surface cannot tell them apart and says only
        // what it knows. The row's next frame carries the truth.
        showReply("Could not send.");
        return;
      }
      // Cycle 450: the minutes left ride the server's `custody_until` on
      // the bound row (read for the `capped` refusal line only).
      showReply(checkinReplyText(outcome, memberName, episode.treatingRemainingMs), {
        transient: checkinReplyIsTransient(outcome),
      });
    },
    [busy, episode, question, memberName, showReply],
  );

  // Cycle 409: "I need help now" only where there is somebody to ask.
  // Unknown reach (`null`) shows the button — the server is the real gate.
  const canAskForHelp = episode.backupReach == null || episode.backupReach > 0;
  const leftMs = promptRemainingMs(nowMs, episode.promptDeadline);
  // Yes/No answer a DECLARATION. Absent on a quiet window, never refused —
  // and WITHDRAWN once the deadline has passed on this device's clock
  // (the cycle-427 dead-man shape): the server resolves an unanswered
  // deadline itself on its next tick, so an answer offered past it could
  // only be refused. Help and Resume stay; they can only make things louder.
  const deadlinePassed = leftMs === 0;
  const canAnswer = pending && !episode.quietWindow && !deadlinePassed;

  // ── Cycle 447: claim the render ──────────────────────────────────────
  // The server issues a prompt and cannot see whether any surface drew it.
  // This device says it did — once per prompt (keyed on the kind and the
  // server's deadline, not on every re-render tick), as a client-claimed
  // ledger row on the next activation report. It is what lets the episode
  // report flag "a prompt issued with no render row". Telemetry only: it
  // changes nothing on screen and gates nothing.
  const claimedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pending || !question) return;
    const key = `${question}|${episode.promptDeadline ?? ""}`;
    if (claimedRef.current === key) return;
    claimedRef.current = key;
    recordClientEvent("prompt_rendered", {
      kind: question,
      surface: tone,
      prompt_deadline: episode.promptDeadline ?? null,
    });
  }, [pending, question, episode.promptDeadline, tone]);

  return (
    <div
      className={`${styles.root} ${styles.takeover}`}
      data-testid="custody-prompt"
      hidden={hidden || undefined}
      role={pending ? "dialog" : undefined}
      aria-live={pending || reply != null ? "assertive" : undefined}
      aria-label={pending ? (text ?? undefined) : undefined}
    >
      {/* The reply takes the question's OWN row — one line for one line,
          so the block never grows past the shape the harness measured. */}
      {reply != null ? (
        <div className={styles.question} data-testid="custody-prompt-reply" role="status">
          {reply}
        </div>
      ) : pending ? (
        <div className={styles.question} data-testid="custody-prompt-question">
          {text}
          {leftMs != null && (
            <span
              className={`${styles.countdown} tnum`}
              data-testid="custody-prompt-countdown"
            >
              {fmtPromptRemaining(leftMs)}
            </span>
          )}
        </div>
      ) : null}
      <div className={styles.actions}>
        {canAnswer && (
          <>
            <button
              type="button"
              className={styles.action}
              data-testid="custody-prompt-yes"
              disabled={busy === "yes"}
              onClick={() => void send("yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.action} ${styles.no}`}
              data-testid="custody-prompt-no"
              disabled={busy === "no"}
              onClick={() => void send("no")}
            >
              No
            </button>
          </>
        )}
        {canAskForHelp && (
          <button
            type="button"
            className={`${styles.action} ${styles.help}`}
            data-testid="custody-prompt-help"
            disabled={busy === "help"}
            onClick={() => void send("help")}
          >
            I need help now
          </button>
        )}
        <button
          type="button"
          className={`${styles.action} ${styles.resume}`}
          data-testid="custody-prompt-resume"
          disabled={busy === "resume"}
          onClick={() => void send("resume")}
        >
          Resume alerts
        </button>
      </div>
    </div>
  );
}
